import React, { useState, useEffect } from 'react';
import { ArrowLeft, Phone, Send, Calendar, Clock, CheckCircle2, Sun, Thermometer, Wind } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  ['aircon-servicing', 'Air Conditioning Servicing — R499'],
  ['aircon-repairs', 'Aircon Repairs'],
  ['heating-service', 'Heating System Service'],
  ['regassing', 'Regassing'],
  ['split-installation', 'Split Unit Installation'],
  ['compressor-replacement', 'Compressor Replacement'],
  ['cold-room', 'Cold Room Repairs'],
  ['other', 'Other'],
];

const BookingPage = () => {
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [dayName, setDayName] = useState('');
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [booked, setBooked] = useState(false);

  // Generate next 14 days for date selection
  const getDateOptions = () => {
    const dates = [];
    const today = new Date();
    for (let i = 1; i <= 14; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      if (d.getDay() !== 0) { // Skip Sundays
        dates.push({
          value: d.toISOString().split('T')[0],
          label: d.toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' }),
          weekday: d.toLocaleDateString('en-ZA', { weekday: 'long' }),
          isTuesday: d.getDay() === 2,
        });
      }
    }
    return dates;
  };

  const dateOptions = getDateOptions();

  useEffect(() => {
    if (!selectedDate) return;
    setLoadingSlots(true);
    setSelectedSlot('');
    axios.get(`${API}/bookings/available?date=${selectedDate}`)
      .then(res => {
        setSlots(res.data.slots || []);
        setDayName(res.data.day || '');
        if (!res.data.available) toast.error(res.data.message);
      })
      .catch(() => toast.error('Failed to load available times'))
      .finally(() => setLoadingSlots(false));
  }, [selectedDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedSlot) {
      toast.error('Please select a date and time');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...form,
        date: selectedDate,
        time_slot: selectedSlot,
      });
      setBooked(true);
      toast.success('Booking confirmed!');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Booking failed. Please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // --- Success Screen ---
  if (booked) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center p-4">
        <Card className="bg-white/5 border border-white/10 max-w-lg w-full">
          <CardContent className="p-10 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-3">Booking Confirmed!</h2>
            <p className="text-gray-400 mb-6">
              Your appointment is booked for <strong className="text-white">{new Date(selectedDate).toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long' })}</strong> at <strong className="text-[#00D9FF]">{selectedSlot}</strong>.
            </p>
            <p className="text-gray-500 text-sm mb-8">We've sent a confirmation to your email. We'll be in touch to confirm details.</p>
            <div className="flex flex-col gap-3">
              <Button className="bg-gradient-to-r from-[#0066FF] to-[#00D9FF] text-white font-bold w-full">
                <a href="/" className="flex items-center justify-center space-x-2 w-full">
                  <ArrowLeft className="w-4 h-4" /><span>Back to Home</span>
                </a>
              </Button>
              <Button className="bg-white/10 border border-white/20 text-white w-full">
                <a href="tel:0644505026" className="flex items-center justify-center space-x-2 w-full">
                  <Phone className="w-4 h-4" /><span>Call: 064 450 5026</span>
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E27]">
      {/* Hero Banner */}
      <div className="relative bg-gradient-to-br from-[#0A0E27] via-[#0D1233] to-[#0A0E27] py-16 overflow-hidden">
        <div className="absolute top-10 right-10 w-[300px] h-[300px] bg-[#0066FF]/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-10 left-10 w-[200px] h-[200px] bg-[#00D9FF]/10 rounded-full blur-[80px]" />
        <div className="container mx-auto px-4 relative z-10">
          <a href="/" className="inline-flex items-center space-x-2 text-gray-400 hover:text-[#00D9FF] transition-colors mb-6" data-testid="back-home">
            <ArrowLeft className="w-4 h-4" /><span>Back to Chill Point</span>
          </a>
          <div className="flex items-center space-x-3 mb-4">
            <Sun className="w-8 h-8 text-yellow-400" />
            <span className="text-sm font-bold uppercase tracking-wider text-yellow-400">Summer 2026</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-4">
            Get Summer Ready
          </h1>
          <p className="text-base md:text-lg text-gray-400 max-w-2xl mb-6">
            Don't wait for the heat to hit. Book your aircon service now and stay cool all summer long.
            Pick a date and time that works for you — we'll handle the rest.
          </p>
          <div className="flex flex-wrap gap-4 text-sm">
            {[
              { icon: <Wind className="w-4 h-4" />, text: 'Full AC Service — R499' },
              { icon: <Thermometer className="w-4 h-4" />, text: 'All Brands Serviced' },
              { icon: <Clock className="w-4 h-4" />, text: '1 Hour Appointments' },
            ].map((item, i) => (
              <div key={i} className="flex items-center space-x-2 text-[#00D9FF] bg-white/5 border border-white/10 px-4 py-2 rounded-full">
                {item.icon}<span className="font-semibold">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Booking Form */}
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-10">
          {[
            { num: 1, label: 'Pick a Date' },
            { num: 2, label: 'Choose Time' },
            { num: 3, label: 'Your Details' },
          ].map((s, i) => (
            <React.Fragment key={s.num}>
              <div className="flex items-center space-x-2">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                  step >= s.num ? 'bg-gradient-to-br from-[#0066FF] to-[#00D9FF] text-white' : 'bg-white/10 text-gray-500'
                }`}>{s.num}</div>
                <span className={`text-sm font-medium hidden sm:inline ${step >= s.num ? 'text-white' : 'text-gray-500'}`}>{s.label}</span>
              </div>
              {i < 2 && <div className={`w-12 h-0.5 mx-3 ${step > s.num ? 'bg-[#00D9FF]' : 'bg-white/10'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Date */}
        {step >= 1 && (
          <div className="mb-8" data-testid="step-date">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center space-x-2">
              <Calendar className="w-5 h-5 text-[#00D9FF]" /><span>Select a Date</span>
            </h3>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
              {dateOptions.map((d) => (
                <button
                  key={d.value}
                  onClick={() => { setSelectedDate(d.value); setStep(2); }}
                  className={`p-3 rounded-xl text-center transition-all text-sm ${
                    selectedDate === d.value
                      ? 'bg-gradient-to-br from-[#0066FF] to-[#00D9FF] text-white shadow-lg shadow-blue-500/25 scale-105'
                      : 'bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:border-[#00D9FF]/30'
                  } ${d.isTuesday ? 'ring-1 ring-yellow-400/30' : ''}`}
                  data-testid={`date-${d.value}`}
                >
                  <div className="font-bold">{d.label.split(' ')[0]}</div>
                  <div className="text-xs mt-1">{d.label.split(' ').slice(1).join(' ')}</div>
                  {d.isTuesday && <div className="text-[10px] text-yellow-400 mt-1">Full Day</div>}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Time Slot */}
        {step >= 2 && selectedDate && (
          <div className="mb-8" data-testid="step-time">
            <h3 className="text-lg font-bold text-white mb-2 flex items-center space-x-2">
              <Clock className="w-5 h-5 text-[#00D9FF]" /><span>Choose a Time — {dayName}</span>
            </h3>
            <p className="text-gray-500 text-sm mb-4">Each appointment is approximately 1 hour</p>
            {loadingSlots ? (
              <div className="text-gray-400 py-8 text-center">Loading available times...</div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-3">
                {slots.map((slot) => (
                  <button
                    key={slot.time}
                    disabled={!slot.available}
                    onClick={() => { setSelectedSlot(slot.time); setStep(3); }}
                    className={`p-3 rounded-xl text-center text-sm font-medium transition-all ${
                      !slot.available
                        ? 'bg-white/[0.02] text-gray-600 cursor-not-allowed line-through'
                        : selectedSlot === slot.time
                          ? 'bg-gradient-to-br from-[#0066FF] to-[#00D9FF] text-white shadow-lg shadow-blue-500/25 scale-105'
                          : 'bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:border-[#00D9FF]/30'
                    }`}
                    data-testid={`slot-${slot.time}`}
                  >
                    {slot.display.split(' - ')[0]}
                    {!slot.available && <div className="text-[10px] text-red-400 mt-1">Booked</div>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Details */}
        {step >= 3 && selectedSlot && (
          <div data-testid="step-details">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center space-x-2">
              <Send className="w-5 h-5 text-[#00D9FF]" /><span>Your Details</span>
            </h3>

            {/* Summary card */}
            <div className="bg-white/5 border border-[#00D9FF]/20 rounded-xl p-4 mb-6 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Calendar className="w-5 h-5 text-[#00D9FF]" />
                <div>
                  <span className="text-white font-semibold text-sm">{new Date(selectedDate).toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
                  <span className="text-[#00D9FF] font-bold ml-3">{selectedSlot}</span>
                </div>
              </div>
              <button onClick={() => { setStep(1); setSelectedSlot(''); }} className="text-gray-400 hover:text-white text-sm underline">Change</button>
            </div>

            <Card className="bg-white/5 border border-white/10">
              <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="booking-form">
                  <div className="absolute opacity-0 h-0 w-0 overflow-hidden" aria-hidden="true" tabIndex={-1}>
                    <input type="text" name="website" autoComplete="off" />
                  </div>
                  {[
                    { label: 'Name', name: 'name', type: 'text', placeholder: 'Your full name' },
                    { label: 'Email', name: 'email', type: 'email', placeholder: 'your.email@example.com' },
                    { label: 'Phone', name: 'phone', type: 'tel', placeholder: '064 450 5026' },
                  ].map((field) => (
                    <div key={field.name}>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">{field.label} *</label>
                      <Input
                        type={field.type}
                        value={form[field.name]}
                        onChange={(e) => setForm({ ...form, [field.name]: e.target.value })}
                        required
                        placeholder={field.placeholder}
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#00D9FF]"
                        data-testid={`booking-${field.name}`}
                      />
                    </div>
                  ))}
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Service *</label>
                    <Select onValueChange={(v) => setForm({ ...form, service: v })} value={form.service}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="booking-service">
                        <SelectValue placeholder="What do you need?" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map(([val, label]) => (
                          <SelectItem key={val} value={val}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Notes (optional)</label>
                    <Textarea
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="Any details about the job (brand, model, issue)..."
                      rows={3}
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#00D9FF]"
                      data-testid="booking-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting || !form.name || !form.email || !form.phone || !form.service}
                    className="w-full bg-gradient-to-r from-[#0066FF] to-[#00D9FF] hover:from-[#0052CC] hover:to-[#00B8D4] text-white text-lg py-6 font-bold shadow-lg shadow-blue-500/25"
                    data-testid="confirm-booking-btn"
                  >
                    {submitting ? 'Booking...' : 'Confirm Booking'}
                  </Button>
                  <p className="text-xs text-gray-500 text-center">
                    By booking, you agree to our{' '}
                    <a href="/privacy-policy" className="text-[#00D9FF] underline">Privacy Policy</a> and{' '}
                    <a href="/terms-of-service" className="text-[#00D9FF] underline">Terms</a>.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingPage;
